import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';
import './SupportMessages.css';

function avatarFor(nameOrId) {
  const initial = String(nameOrId || 'U').trim().slice(0, 1).toUpperCase() || 'U';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="48" fill="#15803d"/><text x="48" y="58" text-anchor="middle" font-family="Arial, sans-serif" font-size="38" font-weight="700" fill="#ffffff">${initial}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function displayTime(value) {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return date.toLocaleString();
}

export default function SupportMessages() {
  const { copy } = useAdminLanguage();
  const text = copy.supportMessages;
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState('');

  const loadConversations = useCallback(async (preferredId = null) => {
    setLoading(true);
    try {
      const response = await api.get('/messages/support/conversations');
      const next = response.data.conversations || [];
      setConversations(next);
      setActive(previous => {
        const targetId = preferredId || previous?.id;
        const nextActive = next.find(item => Number(item.id) === Number(targetId)) || next[0] || null;
        if (previous && nextActive && Number(previous.id) === Number(nextActive.id)) return previous;
        return nextActive;
      });
    } catch (err) {
      setError(err.response?.data?.error || text.errors.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [text.errors.loadFailed]);

  const loadMessages = useCallback(async (conversation) => {
    if (!conversation?.id) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    try {
      const response = await api.get(`/messages/support/conversations/${conversation.id}/messages`);
      setMessages(response.data.messages || []);
      await api.post(`/messages/support/conversations/${conversation.id}/read`);
      setConversations(previous => previous.map(item => (
        Number(item.id) === Number(conversation.id) ? { ...item, unread: 0 } : item
      )));
    } catch (err) {
      setError(err.response?.data?.error || text.errors.messagesFailed);
    } finally {
      setLoadingMessages(false);
    }
  }, [text.errors.messagesFailed]);

  useEffect(() => {
    const refresh = () => {
      loadConversations(active?.id || null);
      if (active?.id) loadMessages(active);
    };
    refresh();
    const timer = window.setInterval(refresh, 15000);
    return () => window.clearInterval(timer);
  }, [active, loadConversations, loadMessages]);

  const filteredConversations = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter(item => (
      String(item.peer_name || '').toLowerCase().includes(query)
      || String(item.last_body || '').toLowerCase().includes(query)
    ));
  }, [conversations, keyword]);

  const selectConversation = (conversation) => {
    setError('');
    setActive(conversation);
  };

  const send = async () => {
    const body = input.trim();
    if (!body || !active?.id || sending) return;
    setSending(true);
    setError('');
    try {
      const response = await api.post(`/messages/support/conversations/${active.id}/messages`, { body });
      setMessages(previous => [...previous, response.data]);
      setInput('');
      await loadConversations(active.id);
    } catch (err) {
      setError(err.response?.data?.error || text.errors.sendFailed);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="support-messages-page">
      <div className="page-title">✉ {text.title}</div>
      {error && <div className="card support-message-error">{error}</div>}

      <div className="support-messages-layout">
        <section className="card support-inbox">
          <div className="support-inbox-head">
            <strong>{text.inboxTitle}</strong>
            <span>{text.conversationCount(conversations.length)}</span>
          </div>
          <input
            className="support-search"
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            placeholder={text.searchPlaceholder}
          />
          <div className="support-conversation-list">
            {loading ? (
              <div className="support-empty">{text.loading}</div>
            ) : filteredConversations.length === 0 ? (
              <div className="support-empty">{text.empty}</div>
            ) : filteredConversations.map(conversation => (
              <button
                type="button"
                className={`support-conversation ${Number(active?.id) === Number(conversation.id) ? 'active' : ''}`}
                key={conversation.id}
                onClick={() => selectConversation(conversation)}
              >
                <img
                  src={conversation.peer_avatar || avatarFor(conversation.peer_name || conversation.peer_id)}
                  alt=""
                  className="support-avatar"
                />
                <span className="support-conversation-copy">
                  <span className="support-conversation-row">
                    <strong>{conversation.peer_name || text.userFallback(conversation.peer_id)}</strong>
                    <time>{displayTime(conversation.last_message_at || conversation.created_at)}</time>
                  </span>
                  <span className="support-last-message">{conversation.last_body || text.noMessages}</span>
                </span>
                {Number(conversation.unread) > 0 && <span className="support-unread">{conversation.unread}</span>}
              </button>
            ))}
          </div>
        </section>

        <section className="card support-thread">
          {active ? (
            <>
              <header className="support-thread-head">
                <div className="support-thread-identity">
                  <img
                    src={active.peer_avatar || avatarFor(active.peer_name || active.peer_id)}
                    alt=""
                    className="support-avatar support-avatar-large"
                  />
                  <div>
                    <strong>{active.peer_name || text.userFallback(active.peer_id)}</strong>
                    <span>{text.supportTag}</span>
                  </div>
                </div>
                <span className="support-thread-id">#{active.peer_id}</span>
              </header>
              <div className="support-thread-messages">
                {loadingMessages ? (
                  <div className="support-empty">{text.loadingMessages}</div>
                ) : messages.length === 0 ? (
                  <div className="support-empty">{text.noMessages}</div>
                ) : messages.map(message => {
                  const fromUser = Number(message.sender_id) === Number(active.peer_id);
                  return (
                    <div className={`support-message-row ${fromUser ? 'from-user' : 'from-admin'}`} key={message.id}>
                      <div className="support-message-bubble">
                        <div>{message.body}</div>
                        <time>{displayTime(message.created_at)}</time>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="support-composer">
                <textarea
                  value={input}
                  onChange={event => setInput(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      send();
                    }
                  }}
                  placeholder={text.replyPlaceholder}
                  rows={3}
                />
                <button className="btn btn-primary" type="button" disabled={!input.trim() || sending} onClick={send}>
                  {sending ? text.sending : text.send}
                </button>
              </div>
            </>
          ) : (
            <div className="support-thread-empty">{text.selectConversation}</div>
          )}
        </section>
      </div>
    </div>
  );
}
